import { useCallback, useEffect, useState } from "react";
import { apiGet } from "../../../api/fetch";
import { endpoints } from "../../../utils/Constants";
import { mockReservationData, mockRefetchData } from "../../../common/data/reservation.mock";
import type { ReservationData } from "../../../types/reservation";
import { getReservationKeyFromPath } from "../../../components/Utils";
import { useReservationStore } from "../../../stores/useReservationStore";

const USE_MOCK_DATA = false;

// Mock for testing product reconciliation after a 40xxx Product submit error.
const MOCK_REFETCH_PRODUCTS =
  USE_MOCK_DATA ? (mockRefetchData.data.products ?? null) : null;

interface UseReservationDataResult {
  data: ReservationData | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<boolean>;
}

export function useReservationData(): UseReservationDataResult {
  const setReservationStore = useReservationStore((s) => s.setReservationStore);
  const reconcileProducts = useReservationStore((s) => s.reconcileProducts);
  const [data, setData] = useState<ReservationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (USE_MOCK_DATA) {
      setData(mockReservationData.data);
      setReservationStore(mockReservationData.data);
      setLoading(false);
      return;
    }

    const requestKey = getReservationKeyFromPath();
    setLoading(true);
    setError(null);

    apiGet(endpoints.reservation.getReservation(requestKey))
      .then((result) => {
        if (result.data?.data) {
          setData(result.data.data);
          setReservationStore(result.data.data);
        } else {
          throw new Error("No reservation data found");
        }
      })
      .catch((err) => {
        console.error("Can't fetch reservation!", err);
        setError(
          err.message || "Failed to load reservation. Please try again.",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setReservationStore]);

  // Re-fetches reservation data and reconciles the cart without resetting quantities.
  // Returns true on success, false on failure — callers must check before acting on the result.
  const refetch = useCallback(async (): Promise<boolean> => {
    if (MOCK_REFETCH_PRODUCTS) {
      reconcileProducts(MOCK_REFETCH_PRODUCTS);
      return true;
    }
    setError(null);
    setLoading(true);

    const requestKey = getReservationKeyFromPath();

    try {
      const result = await apiGet(
        endpoints.reservation.getReservation(requestKey),
      );
      if (result.data?.data) {
        const freshData: ReservationData = result.data.data;
        setData(freshData);
        reconcileProducts(freshData.products ?? []);
        return true;
      } else {
        throw new Error("No reservation data found");
      }
    } catch (err: any) {
      console.error("Can't refetch reservation!", err);
      setError(err.message || "Failed to load reservation. Please try again.");
      return false;
    } finally {
      setLoading(false);
    }
  }, [reconcileProducts]);

  return {
    data: USE_MOCK_DATA ? mockReservationData.data : data,
    loading,
    error,
    refetch,
  };
}
